import { useCallback, useEffect, useState } from 'react';
import {
  api, ErreurApi,
  type CouvertureRecherche, type DocumentLigne, type EtatSignatures,
  type FicheDocument, type ResultatDepot,
} from './api';
import { base64De, empreinteLocale, octets } from './fichier';

/**
 * REGISTRE DOCUMENTAIRE (§15) — l'acte est la preuve du droit.
 *
 * ⚠️ CET ÉCRAN MONTRE CE QUE LA RECHERCHE N'A PAS PU REGARDER. Le service
 * n'embarque pas d'OCR : un PDF scanné est conservé, vérifiable, horodaté —
 * mais introuvable par son contenu. Afficher « aucun résultat » sans dire cela
 * ferait conclure à l'absence d'une pièce qui est là. Le compte des documents
 * hors de portée accompagne donc chaque recherche.
 *
 * ⚠️ L'EMPREINTE EST CALCULÉE ICI *AUSSI*, et envoyée pour être COMPARÉE — pas
 * pour être crue. Le serveur recalcule la sienne sur les octets reçus et refuse
 * si les deux diffèrent. C'est ce qui transforme un contrôle d'intégrité
 * décoratif en contrôle du transport.
 */

const TYPES_LIBELLES: Record<string, string> = {
  acte_notarie: 'Acte notarié',
  certificat_situation: 'Certificat de situation juridique',
  piece_identite: "Pièce d'identité",
  plan_bornage: 'Plan de bornage',
  proces_verbal_leve: 'Procès-verbal de levé',
};

const OBJETS = ['parcelle', 'releve', 'mutation', 'titre', 'batiment', 'droit', 'litige', 'titulaire'] as const;

const ETIQUETTE_STATUT: Record<string, string> = {
  actif: 'vert', projet: 'ambre', remplace: 'gris', revoque: 'rouge',
};

const LIBELLE_EXTRACTION: Record<string, string> = {
  extrait: 'indexé',
  non_construit: 'OCR requis — non cherchable',
  non_applicable: 'sans texte',
  echec: 'extraction échouée',
  non_evalue: 'non évalué',
};

export function Documents(
  { peutDeposer, peutRevoquer, identifiantInitial }:
  {
    peutDeposer: boolean; peutRevoquer: boolean;
    /**
     * ⚠️ LE DOCUMENT DEMANDÉ DEPUIS AILLEURS S'OUVRE TOUT DE SUITE. Sans cela,
     * la recherche universelle amenait sur la liste et laissait l'agent
     * re-chercher ce qu'il venait de trouver — le clic était « presque » juste,
     * ce qui est la forme discrète de l'illusion de fonctionnement.
     */
    identifiantInitial?: string | null;
  },
) {
  const [liste, setListe] = useState<DocumentLigne[]>([]);
  const [couverture, setCouverture] = useState<CouvertureRecherche | null>(null);
  const [recherche, setRecherche] = useState('');
  const [applique, setApplique] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState<string | null>(identifiantInitial ?? null);
  const [depot, setDepot] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const r = await api.documents.liste(applique ? { recherche: applique } : {});
      setListe(r.documents ?? []);
      setCouverture(r.couvertureRecherche ?? null);
    } catch (e) {
      const err = e as ErreurApi;
      setErreur(err.estIndisponibilite
        ? `${err.message} — le coffre n'est pas configuré sur cette instance. Ce n'est pas une panne du registre.`
        : err.estRefus ? "Votre rôle ne permet pas de consulter le registre documentaire (doc.document:lire)."
        : String(err.message ?? e));
    }
  }, [applique]);

  /* ⚠️ ON SUIT LES DEMANDES SUIVANTES, pas seulement la première : l'état
     initial d'un `useState` est figé au montage, et l'écran reste monté d'une
     recherche à l'autre. Un second clic sur un autre document n'aurait rien
     fait. */
  useEffect(() => { if (identifiantInitial) setOuvert(identifiantInitial); }, [identifiantInitial]);

  useEffect(() => { void charger(); }, [charger]);

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>📄 Registre documentaire</h2>
        <div className="actions">
          <form onSubmit={(e) => { e.preventDefault(); setApplique(recherche.trim()); }}>
            <input value={recherche} onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher dans le contenu…" aria-label="Recherche plein texte" />
          </form>
          {applique && <button className="bouton discret" onClick={() => { setRecherche(''); setApplique(''); }}>Effacer</button>}
          {peutDeposer && <button className="bouton primaire" onClick={() => setDepot(true)}>Déposer une pièce</button>}
        </div>
      </header>

      <div className="avertissement">
        <strong>Les pièces sont chiffrées au repos et jamais supprimées.</strong> Chaque document
        est rangé sous l'empreinte de son contenu ; à la relecture, l'empreinte est recalculée et
        confrontée à ce nom. Un acte se <em>révoque</em> — daté et motivé — il ne s'efface pas :
        il faut pouvoir instruire ce qui a été décidé sur sa foi.
      </div>

      {/* ⚠️ Le témoin de couverture, à côté du résultat et jamais après. */}
      {applique && couverture && (
        <div className={couverture.hors_portee_ocr > 0 ? 'avertissement' : 'mention'}>
          Recherche « {applique} » : <strong>{liste.length} résultat(s)</strong> —
          {' '}portée sur <strong>{couverture.cherchables}</strong> document(s) indexé(s)
          {' '}sur {couverture.total} visible(s). {couverture.note}
        </div>
      )}

      {message && <p className="succes">{message}</p>}
      {erreur && <p className="erreur">{erreur}</p>}

      {depot && <FormulaireDepot
        onFerme={() => setDepot(false)}
        onDepose={(r) => { setDepot(false); setMessage(r.message); setOuvert(r.document.identifiant); void charger(); }}
        onErreur={setErreur}
      />}

      {liste.length === 0 && !erreur && (
        <p className="mention">
          {applique ? 'Aucune pièce indexée ne porte ces mots.' : 'Aucune pièce au registre.'}
        </p>
      )}

      <table className="tableau large">
        <thead>
          <tr><th>Identifiant</th><th>Type</th><th>Émetteur</th><th>Date</th><th>Statut</th>
              <th>Taille</th><th>Indexation</th><th>Rattaché à</th><th /></tr>
        </thead>
        <tbody>
          {liste.map((d) => (
            <tr key={d.identifiant}>
              <td><code>{d.identifiant}</code>{d.version > 1 && <span className="aide"> v{d.version}</span>}</td>
              <td>{TYPES_LIBELLES[d.type_code] ?? d.type_code}</td>
              <td>{d.emetteur}</td>
              <td>{String(d.date_document).slice(0, 10)}</td>
              <td><span className={`etiquette ${ETIQUETTE_STATUT[d.statut] ?? 'gris'}`}>{d.statut}</span></td>
              <td>{octets(d.taille_octets)}</td>
              <td><span className={`etiquette ${d.extraction_statut === 'extrait' ? 'vert' : 'gris'}`}>
                {LIBELLE_EXTRACTION[d.extraction_statut] ?? d.extraction_statut}</span></td>
              <td>{d.rattachements}</td>
              <td><button className="bouton discret" onClick={() => setOuvert(d.identifiant)}>Ouvrir</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {ouvert && <PanneauDocument
        identifiant={ouvert} peutRevoquer={peutRevoquer}
        onFermer={() => setOuvert(null)}
        onChange={(m) => { setMessage(m); void charger(); }}
      />}
    </div>
  );
}

/** Calcule l'empreinte côté navigateur, quand le contexte le permet. */
function FormulaireDepot({ onFerme, onDepose, onErreur }: {
  onFerme: () => void; onDepose: (r: ResultatDepot) => void; onErreur: (m: string) => void;
}) {
  const [typeCode, setTypeCode] = useState('acte_notarie');
  const [emetteur, setEmetteur] = useState('');
  const [dateDocument, setDateDocument] = useState(new Date().toISOString().slice(0, 10));
  const [sensibilite, setSensibilite] = useState(2);
  const [objetType, setObjetType] = useState<string>('parcelle');
  const [objet, setObjet] = useState('');
  const [role, setRole] = useState('acte');
  const [motif, setMotif] = useState('');
  const [fichier, setFichier] = useState<File | null>(null);
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    if (!fichier) { onErreur('Aucun fichier choisi.'); return; }
    setEnvoi(true);
    try {
      const brut = await fichier.arrayBuffer();
      const r = await api.documents.deposer({
        typeCode, emetteur, dateDocument, sensibilite,
        typeMime: fichier.type || 'application/octet-stream',
        contenuBase64: base64De(brut),
        empreinteAnnoncee: await empreinteLocale(brut),
        rattachements: [{ objetType, objet: objet.trim(), role }],
        motif,
      });
      onDepose(r);
    } catch (err) {
      const e2 = err as ErreurApi;
      onErreur(`${e2.message}${e2.details ? ` — ${JSON.stringify(e2.details)}` : ''}`);
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire" onSubmit={soumettre}>
      <h3>Déposer une pièce</h3>
      <div className="champs">
        <label>Type
          <select value={typeCode} onChange={(e) => setTypeCode(e.target.value)}>
            {Object.entries(TYPES_LIBELLES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label>Émetteur
          <input value={emetteur} onChange={(e) => setEmetteur(e.target.value)} required
            placeholder="Étude notariale, administration…" />
        </label>
        <label>Date du document
          <input type="date" value={dateDocument} onChange={(e) => setDateDocument(e.target.value)} required />
        </label>
        <label>Sensibilité <span className="aide">0 public · 1 interne · 2 nominatif · 3 confidentiel</span>
          <select value={sensibilite} onChange={(e) => setSensibilite(Number(e.target.value))}>
            {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label>Se rapporte à
          <select value={objetType} onChange={(e) => setObjetType(e.target.value)}>
            {OBJETS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label>Identifiant de l'objet
          <input value={objet} onChange={(e) => setObjet(e.target.value)} required
            placeholder="GN-PAR-… , GN-LEV-… ou UUID" />
        </label>
        <label>Rôle de la pièce
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {['acte', 'plan', 'decision', 'piece_justificative'].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
      </div>
      <label>Fichier
        <input type="file" onChange={(e) => setFichier(e.target.files?.[0] ?? null)} required />
      </label>
      {fichier && <p className="mention">{fichier.name} — {octets(fichier.size)} — {fichier.type || 'type inconnu'}</p>}
      <label>Motif du dépôt <span className="aide">consigné au journal</span>
        <input value={motif} onChange={(e) => setMotif(e.target.value)} required minLength={3} />
      </label>
      <p className="mention">
        Une pièce doit se rapporter à un objet du registre : la base refuse un document
        rattaché à rien. L'empreinte est calculée sur ce poste <em>et</em> par le serveur,
        puis comparée — un fichier altéré pendant le transfert est refusé.
      </p>
      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Annuler</button>
        <button className="bouton primaire" disabled={envoi}>{envoi ? 'Chiffrement et dépôt…' : 'Déposer'}</button>
      </div>
    </form>
  );
}

function PanneauDocument({ identifiant, peutRevoquer, onFermer, onChange }: {
  identifiant: string; peutRevoquer: boolean; onFermer: () => void; onChange: (m: string) => void;
}) {
  const [fiche, setFiche] = useState<FicheDocument | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [jeton, setJeton] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try { setFiche(await api.documents.fiche(identifiant)); }
    catch (e) { setErreur(String((e as Error).message ?? e)); }
  }, [identifiant]);
  useEffect(() => { void charger(); }, [charger]);

  async function revoquer() {
    const motif = window.prompt('Révoquer cette pièce — motif OBLIGATOIRE (10 caractères minimum) :');
    if (motif === null) return;
    if (motif.trim().length < 10) { setErreur('Une révocation sans motif circonstancié ne peut pas être enregistrée.'); return; }
    setOccupe(true); setErreur(null);
    try { const r = await api.documents.revoquer(identifiant, motif); onChange(r.message); await charger(); }
    catch (e) { setErreur(String((e as Error).message ?? e)); }
    finally { setOccupe(false); }
  }

  async function emettre() {
    setOccupe(true); setErreur(null);
    try {
      const r = await api.documents.emettreJeton(identifiant);
      setJeton(r.jeton);
      onChange(`Jeton de vérification émis pour ${identifiant}.`);
      await charger();
    } catch (e) { setErreur(String((e as Error).message ?? e)); }
    finally { setOccupe(false); }
  }

  const d = fiche?.document;
  return (
    <aside className="panneau">
      <header className="panneau-entete">
        <h3>Pièce <code>{identifiant}</code></h3>
        <button className="fermer" onClick={onFermer} aria-label="Fermer la fiche">×</button>
      </header>

      {erreur && <p className="erreur">{erreur}</p>}
      {!fiche && !erreur && <p className="attente">Chargement…</p>}

      {d && (
        <>
          <dl className="champs">
            <div><dt>Statut</dt><dd><span className={`etiquette ${ETIQUETTE_STATUT[d.statut] ?? 'gris'}`}>{d.statut}</span></dd></div>
            <div><dt>Type</dt><dd>{TYPES_LIBELLES[d.type_code] ?? d.type_code}</dd></div>
            <div><dt>Émetteur</dt><dd>{d.emetteur}</dd></div>
            <div><dt>Date</dt><dd>{String(d.date_document).slice(0, 10)} · version {d.version}</dd></div>
            <div><dt>Taille</dt><dd>{octets(d.taille_octets)} · {d.type_mime}</dd></div>
            <div><dt>Chiffré au repos</dt><dd>{d.chiffre ? `oui (clé ${d.chiffrement_cle_id})` : 'NON'}</dd></div>
            <div><dt>Sensibilité</dt><dd>{d.sensibilite}</dd></div>
            <div><dt>Indexation</dt><dd>{LIBELLE_EXTRACTION[d.extraction_statut] ?? d.extraction_statut}</dd></div>
            <div><dt>Déposé par</dt><dd>{d.depose_par ?? '—'}</dd></div>
            {d.remplace_par && <div><dt>Remplacé par</dt><dd><code>{d.remplace_par}</code></dd></div>}
            {d.motif_revocation && <div><dt>Motif de révocation</dt><dd>{d.motif_revocation}</dd></div>}
          </dl>

          <h4>Empreinte SHA-256</h4>
          <pre className="brut">{d.empreinte_sha256}</pre>
          <p className="mention">
            Recalculez-la sur votre exemplaire : elle doit être identique, caractère pour caractère.
            C'est ce qui distingue une copie du document du document lui-même.
          </p>

          <h4>Se rapporte à ({fiche.rattachements.length})</h4>
          {fiche.rattachements.length === 0
            ? <p className="mention">Aucun — impossible en principe : la base refuse un document rattaché à rien.</p>
            : <ul className="liste">{fiche.rattachements.map((r, i) => (
                <li key={i}><strong>{r.objet_type}</strong> {r.objet_libelle ?? r.objet_id} <span className="aide">({r.role})</span></li>
              ))}</ul>}

          <h4>Jetons de vérification ({fiche.jetons.length})</h4>
          {fiche.jetons.length === 0
            ? <p className="mention">Aucun jeton émis. Un jeton permet à un tiers de vérifier l'authenticité sans compte.</p>
            : <ul className="liste">{fiche.jetons.map((j, i) => (
                <li key={i}>
                  <code>{j.jeton_debut}</code> · {j.consultations} consultation(s)
                  {j.revoque_le && <span className="etiquette rouge"> révoqué</span>}
                  {j.expire_le && <span className="aide"> expire le {String(j.expire_le).slice(0, 10)}</span>}
                </li>
              ))}</ul>}

          {jeton && (
            <div className="avertissement">
              <strong>Jeton émis.</strong> Adresse à imprimer en QR :
              <pre className="brut">{`${location.origin}/verify/document/${jeton}`}</pre>
              Le code <em>désigne</em> le document ; il n'en porte aucune donnée. La page publique
              rend authentique / révoqué / inconnu — jamais le contenu.
            </div>
          )}

          <SectionSignatures identifiant={identifiant} />

          <div className="actions">
            <a className="bouton" href={api.documents.urlContenu(identifiant)} download>Télécharger</a>
            <button className="bouton discret" disabled={occupe || d.statut === 'revoque'} onClick={emettre}>
              Émettre un jeton
            </button>
            {peutRevoquer && d.statut !== 'revoque' && (
              <button className="bouton discret danger" disabled={occupe} onClick={revoquer}>Révoquer</button>
            )}
          </div>
        </>
      )}
    </aside>
  );
}

/**
 * §43 — L'ÉTAT DES SIGNATURES D'UN ACTE.
 *
 * ⚠️ IL VIT DANS LA GED ET NON DANS UNE RUBRIQUE À PART, et ce n'est pas un
 * rangement : ON SIGNE UN DOCUMENT. Partir d'un écran « Signatures » obligerait
 * à retrouver l'acte ensuite ; partir de l'acte, c'est le geste réel. Cela
 * évite en outre une 27ᵉ rubrique — et le cahier prévient lui-même, à son §76,
 * que 60 écrans indépendants deviennent illisibles.
 *
 * ⚠️⚠️ CE QUE CETTE SECTION EXISTE POUR MONTRER : une signature porte sur une
 * EMPREINTE, jamais sur un document (migration 160). Si l'acte est réédité,
 * l'ancienne signature reste valide — sur une version qui n'est plus la
 * courante. Elle est alors détectablement CADUQUE, et c'est le seul état qu'un
 * écran ne doit jamais taire : un acte signé sur une version disparue se
 * présente exactement comme un acte signé.
 */
function SectionSignatures({ identifiant }: { identifiant: string }) {
  const [e, setE] = useState<EtatSignatures | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    setE(null); setErreur(null);
    api.signatures.etat(identifiant)
      .then((r) => { if (vivant) setE(r); })
      .catch((x) => {
        if (!vivant) return;
        setErreur(x instanceof ErreurApi && x.estRefus
          ? "Votre rôle ne permet pas de consulter les signatures (doc.signature:lire)."
          : String((x as Error).message ?? x));
      });
    return () => { vivant = false; };
  }, [identifiant]);

  if (erreur) return <p className="note-indispo">{erreur}</p>;
  if (!e) return <p className="vide">Lecture des signatures…</p>;

  const caduques = e.signatures.filter((s) => !s.correspond_a_l_acte && s.statut !== 'revoquee');

  return (
    <section>
      <h4>Signatures</h4>

      {/**
        * ⚠️ L'AVERTISSEMENT PASSE AVANT LA LISTE. Placé après, il commenterait
        * ce que l'agent a déjà lu comme valide.
        */}
      {caduques.length > 0 && (
        <div className="avertissement danger">
          <strong>{caduques.length} signature(s) ne correspondent plus à cet acte.</strong>
          <p className="proj-refus">
            Elles ont été portées sur une version antérieure. L'acte a été réédité depuis :
            elles restent authentiques, et elles ne valent plus pour le document courant.
            Déterminez quelle version fait foi AVANT d'en délivrer une.
          </p>
        </div>
      )}

      {e.signatures.length === 0 ? (
        /**
         * ⚠️ « AUCUNE SIGNATURE » N'EST PAS « NON SIGNABLE ». Un acte non signé
         * est un acte en attente, pas un acte défaillant — et les deux
         * s'affichent pareil sur une liste vide.
         */
        <p className="vide">
          Aucune signature portée. L'acte existe et n'engage encore personne :
          ce n'est pas un défaut, c'est une étape.
        </p>
      ) : (
        <table className="tableau">
          <thead>
            <tr><th>Signataire</th><th>Rôle</th><th>Procédé</th><th>Date</th><th>Empreinte</th><th>Preuve</th></tr>
          </thead>
          <tbody>
            {e.signatures.map((s) => (
              <tr key={s.id} className={s.statut === 'revoquee' ? 'ligne-radiee' : undefined}>
                <td>{s.signataire ?? '—'}{s.qualite ? <span className="aide"> · {s.qualite}</span> : null}</td>
                <td>{s.role}</td>
                <td>{s.procede}</td>
                <td>{s.horodate_le?.slice(0, 10) ?? '—'}{s.lieu ? <span className="aide"> · {s.lieu}</span> : null}</td>
                <td>
                  <span className={`etat ${s.correspond_a_l_acte ? 'vert' : 'rouge'}`}>
                    {s.correspond_a_l_acte ? 'correspond' : 'ne correspond plus'}
                  </span>
                </td>
                {/**
                  * ⚠️ LA PREUVE COMPTE SURTOUT POUR LA MARQUE À LA CROIX. Une
                  * part des parties à un acte foncier guinéen ne sait pas
                  * écrire ; sans pièce déposée, « il a fait une croix » est
                  * invérifiable — et c'est sur ces actes-là que la
                  * contestation porte.
                  */}
                <td>{s.preuve_deposee ? 'déposée' : <span className="aide">aucune</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {e.scellement ? (
        <p className="proj-lecture">
          Scellé le {e.scellement.scelle_le?.slice(0, 10)} par {e.scellement.scelle_par ?? '—'}
          {e.scellement.organisation ? ` (${e.scellement.organisation})` : ''} —{' '}
          {e.scellement.correspond_a_l_acte
            ? "le sceau correspond à l'acte courant."
            : "⚠️ le sceau porte sur une version antérieure : il ne couvre PAS cet acte."}
        </p>
      ) : (
        <p className="note-indispo">
          Aucun scellement. Les signatures engagent leurs auteurs ; le scellement engage l'autorité.
        </p>
      )}
    </section>
  );
}
